import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import {
  Activity,
  AlertCircle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Bell,
  Building2,
  Calendar,
  ChevronRight,
  DollarSign,
  Receipt,
  TrendingUp,
  Users
} from "lucide-react";
import { useState } from "react";

interface StatCardProps {
  title: string;
  value: string | number;
  description: string;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

const StatCard = ({ title, value, description, icon, trend }: StatCardProps) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">
        {title}
      </CardTitle>
      {icon}
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      <div className="flex items-center gap-2">
        {trend && (
          <span
            className={cn(
              "flex items-center text-xs",
              trend.isPositive ? "text-green-500" : "text-red-500"
            )}
          >
            {trend.isPositive ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            {trend.value}%
          </span>
        )}
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </CardContent>
  </Card>
);

interface RecentActivity {
  id: number;
  type: "payment" | "tenant" | "maintenance" | "alert";
  title: string;
  description: string;
  time: string;
  status?: "pending" | "completed" | "failed";
}

const recentActivities: RecentActivity[] = [
  {
    id: 1,
    type: "payment",
    title: "Отримано оплату",
    description: "Орендна плата за квартиру №12",
    time: "15 хв тому",
    status: "completed"
  },
  {
    id: 2,
    type: "tenant",
    title: "Новий орендар",
    description: "Підписано договір на квартиру №5",
    time: "2 год тому",
    status: "completed"
  },
  {
    id: 3,
    type: "maintenance",
    title: "Запит на ремонт",
    description: "Проблема з опаленням у квартирі №8",
    time: "5 год тому",
    status: "pending"
  },
  {
    id: 4,
    type: "alert",
    title: "Прострочений платіж",
    description: "Квартира №15, затримка 5 днів",
    time: "1 день тому",
    status: "failed"
  }
];

const HomeView = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<"day" | "week" | "month">("week");

  const getActivityIcon = (type: RecentActivity["type"]) => {
    switch (type) {
      case "payment":
        return <Receipt className="h-4 w-4 text-green-500" />;
      case "tenant":
        return <Users className="h-4 w-4 text-blue-500" />;
      case "maintenance":
        return <Building2 className="h-4 w-4 text-orange-500" />;
      case "alert":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatusColor = (status: RecentActivity["status"]) => {
    switch (status) {
      case "completed":
        return "bg-green-500";
      case "pending":
        return "bg-yellow-500";
      case "failed":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="container mx-auto p-8 space-y-8"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Вітаємо, Адміністратор! 👋</h1>
          <p className="text-muted-foreground">
            Ось ваш огляд активності та статистики
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => setSelectedPeriod("day")}>
            День
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSelectedPeriod("week")}>
            Тиждень
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSelectedPeriod("month")}>
            Місяць
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Загальний дохід"
          value="₴125,000"
          description="на 12% більше"
          icon={<DollarSign className="h-4 w-4 text-green-500" />}
          trend={{ value: 12, isPositive: true }}
        />
        <StatCard
          title="Активні оренди"
          value="24"
          description="2 нові цього місяця"
          icon={<Building2 className="h-4 w-4 text-blue-500" />}
          trend={{ value: 8, isPositive: true }}
        />
        <StatCard
          title="Заборгованості"
          value="₴15,000"
          description="на 5% менше"
          icon={<AlertCircle className="h-4 w-4 text-red-500" />}
          trend={{ value: 5, isPositive: false }}
        />
        <StatCard
          title="Рівень заповненості"
          value="92%"
          description="висока активність"
          icon={<TrendingUp className="h-4 w-4 text-purple-500" />}
          trend={{ value: 3, isPositive: true }}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Фінансовий огляд</CardTitle>
            <CardDescription>
              Доходи та витрати за {selectedPeriod === "day" ? "день" : selectedPeriod === "week" ? "тиждень" : "місяць"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Дохід</span>
                  <span className="text-sm text-green-500">₴125,000</span>
                </div>
                <Progress value={75} className="h-2 [&>div]:bg-green-500" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Витрати</span>
                  <span className="text-sm text-red-500">₴45,000</span>
                </div>
                <Progress value={35} className="h-2 [&>div]:bg-red-500" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Прибуток</span>
                  <span className="text-sm text-blue-500">₴80,000</span>
                </div>
                <Progress value={60} className="h-2 [&>div]:bg-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Нещодавня активність</CardTitle>
            <CardDescription>
              Останні події та оновлення
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start gap-4 rounded-lg border p-4"
                  >
                    <div className="p-2 bg-primary/10 rounded-full">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{activity.title}</p>
                        <div className="flex items-center gap-2">
                          {activity.status && (
                            <div
                              className={cn(
                                "h-2 w-2 rounded-full",
                                getStatusColor(activity.status)
                              )}
                            />
                          )}
                          <span className="text-xs text-muted-foreground">
                            {activity.time}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {activity.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Майбутні платежі</CardTitle>
            <CardDescription>
              Очікувані надходження
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar>
                    <AvatarImage src="/avatars/01.png" />
                    <AvatarFallback>ІП</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">Іван Петренко</p>
                    <p className="text-sm text-muted-foreground">Кв. №12</p>
                  </div>
                </div>
                <Badge>₴12,000</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar>
                    <AvatarImage src="/avatars/02.png" />
                    <AvatarFallback>МК</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">Марія Ковальчук</p>
                    <p className="text-sm text-muted-foreground">Кв. №5</p>
                  </div>
                </div>
                <Badge>₴8,500</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Технічні запити</CardTitle>
            <CardDescription>
              Активні заявки на обслуговування
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-orange-100 rounded-full">
                  <Building2 className="h-4 w-4 text-orange-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">Проблема з опаленням</p>
                    <Badge variant="outline">Терміново</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">Кв. №8</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Building2 className="h-4 w-4 text-blue-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">Заміна замка</p>
                    <Badge variant="outline">Звичайний</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">Кв. №15</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Календар подій</CardTitle>
            <CardDescription>
              Заплановані зустрічі та огляди
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-purple-100 rounded-full">
                  <Calendar className="h-4 w-4 text-purple-500" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Огляд квартири</p>
                  <p className="text-sm text-muted-foreground">
                    Завтра, 14:00 - Кв. №3
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-2 bg-green-100 rounded-full">
                  <Users className="h-4 w-4 text-green-500" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Підписання договору</p>
                  <p className="text-sm text-muted-foreground">
                    П'ятниця, 11:00 - Кв. №7
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
};

export default HomeView; 